import BoxProjectionPars from "./custom_box_projection.glsl";
import CustomBSDFS from "./custom_bsdfs.glsl";
import CustomEnvMapPars from "./custom_envmap_physical_pars_fragment.glsl";
import CustomBumpmapPars from "./custom_bumpmap_pars_fragment.glsl"
import CustomUvVariantsPars from "./custom_uv_variants_pars_frag.glsl.js";
import CustomAoMapFragment from "./custom_aomap_fragment.glsl.js";
import CustomNormalmapPars from "./custom_normalmap_pars_fragment.glsl.js";
import CustomNormalmapFragment from "./custom_normal_fragment_maps.glsl.js";
import CustomLightsPhysicalFragment from "./custom_lights_physical_fragment.glsl";
import CustomLightsPhysicalParsFragment from "./custom_lights_physical_pars_fragment.glsl";
import CustomLightsFragmentMaps from "./custom_lights_fragment_maps.glsl.js";
import IridescenceSampler from "./iridescence_sampler.glsl.js";
import CustomTonemapFragment from "./custom_tonemap_fragment.glsl";
import CustomTonemappingPars from "./custom_tonemapping_pars_fragment.glsl"
import CustomLightsFragmentBegin from './custom_lights_fragment_begin.glsl'
import CustomLightsParsBegin from "./custom_lights_pars_begin.glsl";

import TriplanarPars from "./triplanar_mapping_pars.glsl"

import UtilityFunctions from "./utility_functions.glsl"

import CustomTriplanarSampling from "./texture-samplers/custom_triplanar_map_samplers.glsl"
import RoughnessSampler from "./texture-samplers/roughness_sampler.glsl"
import MetalnessSampler from "./texture-samplers/metalness_sampler.glsl"
import DiffuseSampler from "./texture-samplers/diffuse_sampler.glsl";
import AOSampler from './texture-samplers/ao_sampler.glsl'
import EmissiveSampler from './texture-samplers/emissive_sampler.glsl'

import OrthographicCubemappingFunctions from "./orthographic_cubemap_pars_frag.glsl";


// eslint-disable-next-line import/no-anonymous-default-export
export default /* glsl */`
#define STANDARD


uniform bool anisotropyDebug;
uniform bool anisotropicReflections;
uniform bool anisotropyEnabled; // high level control
uniform float anisotropyStrength;

uniform bool reflectionDebug;
uniform bool overblowDebug;
uniform float magic1;
uniform float magic2;
uniform float magic3;

uniform bool orthographicCubemapping;

uniform samplerCube ormCubemap;
uniform samplerCube normalCubemap;
uniform samplerCube defaultNormalCubemap;
uniform samplerCube standardCubemap; // this is the regular cubemap, could be handled otherwise too
uniform int reflectionBounces;

// replacement
// also: use a define as a preprocessor parameter?
// see: https://stackoverflow.com/questions/30585265/what-can-i-use-as-an-array-index-in-glsl-in-webgl
// hazard note: if this doesn't get unrolled, hell breaks loose?
// might not work correctly, we are hitting the max number of texture units per shader
//uniform int cubemapArraySize;
//uniform samplerCube[DIFFUSE_CUBEMAP_ARRAY_SIZE] diffuseCubemapArray;

// this is not possible with threejs currently because it uses glsl 3 (300), not 4
//uniform samplerCubeArray testarray;

// TODO: remove this...
//uniform samplerCube diffuseCubemapA;
//uniform samplerCube diffuseCubemapB;
//uniform float diffuseCubemapBlending;
// ...to this

#ifdef PHYSICAL
	#define REFLECTIVITY
	#define CLEARCOAT
	#define TRANSMISSION
#endif

uniform bool uv2AlphaMap;
uniform bool uv1AOMap;

uniform bool diffuseOnly;
uniform bool emissionOnly;
uniform bool ormOnly;
uniform bool normalOnly;
uniform bool opacityToR;
uniform bool noEnvironmentMapping; // replace this with diffuse only
uniform float bloomIntensity;

// old defines as dynamic
uniform bool objectspace_normalmap;
uniform bool flip_sided;
uniform bool double_sided;
uniform bool tangentspace_normalmap;
uniform bool use_tangent;
uniform bool use_bumpmap;

${TriplanarPars}
${CustomTonemappingPars}
${BoxProjectionPars}
${OrthographicCubemappingFunctions}

uniform vec3 diffuse;
uniform vec3 emissive;
uniform float roughness;
uniform float metalness;
uniform float opacity;

#ifdef USE_TRANSMISSION
	uniform float transmission;
	uniform float thickness;
	uniform vec3 attenuationColor;
	uniform float attenuationDistance;
#endif
#ifdef REFLECTIVITY
	uniform float reflectivity;
#endif

#ifdef CLEARCOAT
	uniform float clearcoat;
	uniform float clearcoatRoughness;
#endif

#ifdef USE_SHEEN
	uniform vec3 sheen;
#endif

varying vec3 vViewPosition;

#ifndef FLAT_SHADED

	varying vec3 vNormal;

	#ifdef USE_TANGENT

		varying vec3 vTangent;
		varying vec3 vBitangent;

	#endif

#endif

varying vec3 vWorldPosition;
varying vec3 vAnisotropicTangent;

#include <common>
#include <packing>
#include <dithering_pars_fragment>
#include <color_pars_fragment>
#include <uv_pars_fragment>
#include <uv2_pars_fragment>
${CustomUvVariantsPars}
#include <map_pars_fragment>
#include <alphamap_pars_fragment>
#include <aomap_pars_fragment>
#include <lightmap_pars_fragment>
#include <emissivemap_pars_fragment>

${UtilityFunctions}
${CustomBSDFS}
//#include <bsdfs>
#include <transmission_pars_fragment>
#include <cube_uv_reflection_fragment>
#include <envmap_common_pars_fragment>
//#include <envmap_physical_pars_fragment>
${CustomEnvMapPars}
#include <fog_pars_fragment>
${CustomLightsParsBegin}
//#include <lights_pars_begin>
//#include <lights_physical_pars_fragment>
${CustomLightsPhysicalParsFragment}
#include <shadowmap_pars_fragment>
${CustomBumpmapPars}
//#include <bumpmap_pars_fragment>
//#include <normalmap_pars_fragment>
${CustomNormalmapPars}
#include <clearcoat_pars_fragment>
#include <roughnessmap_pars_fragment>
#include <metalnessmap_pars_fragment>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>

void main() {


	#include <clipping_planes_fragment>
    if (diffuseOnly){
        vec4 diffuseColor = vec4(diffuse, 1.0);
        ${DiffuseSampler}
        vec3 totalEmissiveRadiance = emissive;
        ${EmissiveSampler}
        #ifdef USE_ALPHAMAP
            diffuseColor.a = texture2D( alphaMap, alphaMapUv ).g * opacity;
        #endif
        ${AOSampler}

        // not completely true for this, but we can bake in the lightmap and occlusion perhaps
        diffuseColor.rgb += totalEmissiveRadiance.rgb;
        gl_FragColor = diffuseColor;

        // while not technically diffuse, we can jam in the lightmaps here for later use
        //vec3 lightSample = texture2D( lightMap, vUv2 ).rgb * lightMapIntensity;
        vec3 lightSample = lightMapTexelToLinear( texture2D( lightMap, vUv2 ) ).rgb * lightMapIntensity * 2.0;
        gl_FragColor.rgb *= lightSample.rgb * ambientOcclusion;
        //gl_FragColor.rgb = lightSample.rgb;
        //gl_FragColor.rgb = vec3(magic1);
        ${CustomTonemapFragment}

    } else if (emissionOnly){
        vec4 bloomSample = vec4(vec3(bloomIntensity), 1.0);
        // if and when needed, we can sample the emissionmap to have a stencil
        #ifdef USE_EMISSIVEMAP
            vec4 emissiveSample = texture2D( emissiveMap, emissiveMapUv );
            bloomSample *= emissiveSample;
        #endif
        gl_FragColor = bloomSample;
    } else if (ormOnly){
        float roughnessFactor = roughness;
        float metalnessFactor = metalness;
        ${AOSampler}
        ${RoughnessSampler}
        ${MetalnessSampler}

        // check if we have emission, if so, slap the roughness up so it doesn't eat itself in reflection bounces
        vec3 totalEmissiveRadiance = emissive;
        ${EmissiveSampler}
        float emissionMagnitude = totalEmissiveRadiance.r + totalEmissiveRadiance.g + totalEmissiveRadiance.b;
        emissionMagnitude /= 3.0;
        roughnessFactor = mix(roughnessFactor, 1.0, emissionMagnitude);

        float alpha = 1.0;
        #ifdef USE_ALPHAMAP
        alpha = texture2D( alphaMap, alphaMapUv ).g * opacity;
        #endif
        vec4 final = vec4(ambientOcclusion, roughnessFactor, metalnessFactor, alpha);
        #ifdef USE_ALPHAMAP
        //final.g = 0.0;
        #endif
        // orm: occlusion roughness metalness
        gl_FragColor = final;
    } else if (normalOnly){
        
        #include <normal_fragment_begin>
        ${CustomNormalmapFragment}
        float alpha = opacity;
        #ifdef USE_ALPHAMAP
            alpha = texture2D( alphaMap, alphaMapUv ).g;
        #endif
        vec3 normalSample = inverseTransformDirection( normalize( normal ), viewMatrix ) * 0.5 + 0.5;
        gl_FragColor = vec4(normalSample, alpha);        
    } else if (anisotropyDebug){
        vec4 noAnisoColor = vec4(1.0, 0.0, 0.0, 1.0);
        vec4 lowAnisoColor = vec4(0.0, 0.0, 1.0, 1.0);
        vec4 fullAnisoColor = vec4(0.0, 1.0, 0.0, 1.0);
        if (!anisotropicReflections){
            gl_FragColor = noAnisoColor;
        } else {
            gl_FragColor = mix(lowAnisoColor, fullAnisoColor, anisotropyStrength);
        }
        
    } else if (opacityToR){
        float alpha = opacity;
        #ifdef USE_ALPHAMAP
            alpha *= texture2D( alphaMap, alphaMapUv ).g;
        #endif
        //gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
        gl_FragColor = vec4(1.0, 0.0, 0.0, alpha);
    } else {


        vec4 debugColor = vec4(1.0);

        vec3 totalEmissiveRadiance = emissive;
        
	    vec4 diffuseColor = vec4( diffuse, opacity );
	    ReflectedLight reflectedLight = ReflectedLight( vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ), vec3( 0.0 ) );
	
        #ifdef USE_TRANSMISSION
            float totalTransmission = transmission;
            float thicknessFactor = thickness;
        #endif
        

        float metalnessFactor = metalness;
        float roughnessFactor = roughness;

        #include <logdepthbuf_fragment>
        //#include <map_fragment>
        #include <color_fragment>
        //#include <alphamap_fragment>
        #include <alphatest_fragment>
        //#include <roughnessmap_fragment>
        //#include <metalnessmap_fragment>
        #include <normal_fragment_begin>
        //#include <normal_fragment_maps>
        ${CustomNormalmapFragment}
        #include <clearcoat_normal_fragment_begin>
        #include <clearcoat_normal_fragment_maps>
        //#include <emissivemap_fragment>
        #include <transmission_fragment>

        ${CustomTriplanarSampling}
        #ifdef IRIDESCENT
            ${IridescenceSampler}
        #endif

        // accumulation
        // rawDiffuseColor *= diffuseColor;

        // #include <lights_physical_fragment>

        ${CustomLightsPhysicalFragment}
        //#include <lights_fragment_begin>
        ${CustomLightsFragmentBegin}
        ${CustomLightsFragmentMaps}
        //#include <lights_fragment_maps>
        #include <lights_fragment_end>

        // modulation
        //#include <aomap_fragment>
        ${CustomAoMapFragment}

        vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;

        gl_FragColor = vec4( outgoingLight, diffuseColor.a );

        ${CustomTonemapFragment}
        
        #include <encodings_fragment>
        #include <fog_fragment>
        #include <premultiplied_alpha_fragment>
        #include <dithering_fragment>

        // #ifdef USE_ANISOTROPIC_REFLECTIONS
        //     //debugColor.x = 0.0;
        // #endif

        
        // if (noEnvironmentMapping){
        //     debugColor = vec4(1.0, 0.0, 0.0, 1.0);
        // }

        //debugColor.xyz = inverseTransformDirection( normalize( normal ), viewMatrix );
        //gl_FragColor = debugColor;

        /*
        if (numOfSpotlights < 1){
            gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
        } else if (numOfSpotlights > 0 && numOfSpotlights < 6){
            gl_FragColor = vec4(0.0, 1.0, 0.0, 1.0);
        } else {
            gl_FragColor = vec4(0.0, 0.0, 1.0, 1.0);
        } */
        
        
        // webgl2 could be able to do this magjick
        // gl_FragData[0] = gl_FragColor;
        // gl_FragData[1] = vec4(1.0, 0.0, 0.0, 1.0);

    }
}
`;